/**
 * IntegrationDetail — Single integration page (integrations.example.com/:locale/:slug)
 * Hero, features, setup guide, related integrations
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';

import { T } from '../styles/tokens';
import { ListSkeleton, SkeletonBar } from '../components/ui';
const API_BASE = '/api/v1/cms';

interface IntegrationItem {
  id: string; slug: string; icon_url: string; website_url: string; status: string;
  is_popular: number; min_plan: string; view_count: number; tags: string;
  category_slug: string; category_color: string; category_icon: string; category_name: string;
  name: string; short_description: string; description: string; setup_guide: string; features_json: string;
  meta_title: string; meta_description: string;
  // CMS fields
  title: string; excerpt: string; body: string; metadata: string; custom_fields: string;
}

interface RelatedItem {
  id: string; slug: string; metadata: string; is_popular: number;
  title: string; excerpt: string; category_slug: string; category_color: string; category_name: string;
  // Computed
  icon_url?: string; status?: string; min_plan?: string; name?: string; short_description?: string;
}

function getLocale(): string {
  const path = window.location.pathname.split('/')[1];
  if (path && (path === 'pt-BR' || path === 'en' || path === 'es')) return path;
  return navigator.language?.startsWith('pt') ? 'pt-BR' : 'en';
}

function getSlug(): string {
  const parts = window.location.pathname.split('/');
  return parts[2] || '';
}

const statusLabels: Record<string, { label: string; color: string }> = {
  available: { label: 'Disponivel', color: T.success },
  coming_soon: { label: 'Em breve', color: T.warning },
  beta: { label: 'Beta', color: T.primary },
};

function parseFeatures(json: string): string[] {
  try { return JSON.parse(json || '[]'); } catch { return []; }
}

// Simple markdown renderer (bold, headings, code blocks, links, lists)
function renderMarkdown(md: string): any {
  if (!md) return null;
  const lines = md.split('\n');
  const elements: any[] = [];
  let inCode = false;
  let codeBlock: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('```')) {
      if (inCode) {
        elements.push(h('pre', { style: { background: T.bgCardHover, padding: 16, borderRadius: 8, overflow: 'auto', fontSize: 13, lineHeight: 1.6 } },
          h('code', null, codeBlock.join('\n'))));
        codeBlock = [];
      }
      inCode = !inCode;
      continue;
    }
    if (inCode) { codeBlock.push(line); continue; }
    if (line.startsWith('### ')) {
      elements.push(h('h4', { style: { fontSize: 16, fontWeight: 700, margin: '20px 0 8px', color: T.text } }, line.slice(4)));
    } else if (line.startsWith('## ')) {
      elements.push(h('h3', { style: { fontSize: 18, fontWeight: 700, margin: '24px 0 12px', color: T.text } }, line.slice(3)));
    } else if (line.startsWith('# ')) {
      elements.push(h('h2', { style: { fontSize: 22, fontWeight: 700, margin: '28px 0 12px', color: T.text } }, line.slice(2)));
    } else if (line.match(/^\d+\.\s/)) {
      elements.push(h('p', { style: { margin: '4px 0 4px 20px', color: T.textSecondary, lineHeight: 1.7 } }, line));
    } else if (line.trim() === '') {
      elements.push(h('br', null));
    } else {
      // Process bold
      const parts = line.split(/\*\*(.*?)\*\*/g);
      const children = parts.map((part, idx) => idx % 2 === 1 ? h('strong', null, part) : part);
      elements.push(h('p', { style: { margin: '4px 0', color: T.textSecondary, lineHeight: 1.7 } }, ...children));
    }
  }
  return h('div', null, ...elements);
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif" } as any,
  container: { maxWidth: 1000, margin: '0 auto', padding: '0 20px' } as any,
  header: { padding: '20px 0', borderBottom: `1px solid ${T.border}`, marginBottom: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } as any,
  logo: { fontSize: 20, fontWeight: 700, color: T.text, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 } as any,
  logoAccent: { background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  back: { display: 'inline-flex', alignItems: 'center', gap: 6, color: T.textSecondary, textDecoration: 'none', fontSize: 14, marginBottom: 24, cursor: 'pointer' } as any,
  hero: { display: 'flex', gap: 24, alignItems: 'flex-start', marginBottom: 40 } as any,
  heroIcon: { width: 80, height: 80, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } as any,
  heroInfo: { flex: 1 } as any,
  heroTitle: { fontSize: 32, fontWeight: 800, margin: '0 0 8px' } as any,
  heroDesc: { fontSize: 16, color: T.textSecondary, margin: '0 0 16px', lineHeight: 1.5 } as any,
  badges: { display: 'flex', gap: 8, flexWrap: 'wrap' as const } as any,
  badge: (color: string) => ({ display: 'inline-block', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: `${color}20`, color }) as any,
  layout: { display: 'flex', gap: 40 } as any,
  main: { flex: 1, minWidth: 0 } as any,
  sidebar: { width: 280, flexShrink: 0 } as any,
  section: { marginBottom: 40 } as any,
  sectionTitle: { fontSize: 18, fontWeight: 700, marginBottom: 16 } as any,
  featureList: { listStyle: 'none', padding: 0, margin: 0 } as any,
  featureItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 15, color: T.textSecondary } as any,
  featureCheck: { color: T.success, flexShrink: 0 } as any,
  setupToggle: { background: T.bgCardHover, border: `1px solid ${T.whiteAlpha8}`, borderRadius: 10, padding: '14px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 15, fontWeight: 600 } as any,
  setupContent: { padding: '20px 0' } as any,
  sidebarCard: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 20 } as any,
  sidebarTitle: { fontSize: 13, fontWeight: 700, marginBottom: 14, textTransform: 'uppercase' as const, letterSpacing: '0.5px', color: T.textMuted } as any,
  cta: { display: 'block', width: '100%', padding: '14px', background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: T.text, borderRadius: 10, fontWeight: 700, fontSize: 15, textAlign: 'center' as const, textDecoration: 'none', border: 'none', cursor: 'pointer', boxSizing: 'border-box' as const } as any,
  relatedCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', cursor: 'pointer', borderBottom: `1px solid ${T.borderSubtle}` } as any,
  relatedIcon: { width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 } as any,
  footer: { padding: '40px 0', borderTop: `1px solid ${T.border}`, textAlign: 'center' as const, color: T.textMuted, fontSize: 13, marginTop: 60 } as any,
  loading: { textAlign: 'center' as const, padding: '80px 20px', color: T.textMuted } as any,
  notFound: { textAlign: 'center' as const, padding: '80px 20px' } as any,
};

export function IntegrationDetail() {
  const locale = getLocale();
  const slug = getSlug();
  const [item, setItem] = useState<IntegrationItem | null>(null);
  const [related, setRelated] = useState<RelatedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/entries/${slug}?contentType=integrations&locale=${locale}`)
      .then(r => r.json())
      .then(d => {
        const entry = d.data?.entry;
        if (entry) {
          const meta: any = (() => { try { return JSON.parse(entry.metadata || '{}'); } catch { return {}; } })();
          const cf: any = (() => { try { return JSON.parse(entry.custom_fields || '{}'); } catch { return {}; } })();
          entry.name = entry.title || '';
          entry.short_description = entry.excerpt || '';
          entry.description = entry.body || '';
          entry.icon_url = meta.icon_url || '';
          entry.website_url = meta.website_url || '';
          entry.status = meta.status || 'available';
          entry.min_plan = meta.min_plan || 'free';
          entry.setup_guide = cf.setup_guide || '';
        }
        setItem(entry || null);
        const rel = (d.data?.related || []).map((r: any) => {
          const rm: any = (() => { try { return JSON.parse(r.metadata || '{}'); } catch { return {}; } })();
          return { ...r, name: r.title, short_description: r.excerpt, icon_url: rm.icon_url || '', status: rm.status || 'available', min_plan: rm.min_plan || 'free' };
        });
        setRelated(rel);
        setLoading(false);
        if (entry?.id) {
          fetch(`${API_BASE}/entries/${entry.id}/track`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_type: 'view' }) }).catch(() => {});
        }
      })
      .catch(() => setLoading(false));
  }, [slug, locale]);

  if (loading) return <div style={S.page}><div style={S.loading}><ListSkeleton rows={3} /></div></div>;
  if (!item) return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.notFound}>
          <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Integracao nao encontrada</h2>
          <a href={`/${locale}`} style={{ color: T.primary }}>Voltar para integracoes</a>
        </div>
      </div>
    </div>
  );

  const st = statusLabels[item.status] || statusLabels.available;
  const features = parseFeatures(item.features_json);

  return (
    <div style={S.page}>
      <div style={S.container}>
        <header style={S.header}>
          <a style={S.logo} href={`/${locale}`}>
            <span style={S.logoAccent}>WhiteLabelClaw</span>
            <span style={{ color: T.textMuted, fontWeight: 400 }}>/ Integracoes</span>
          </a>
        </header>

        <a style={S.back} onClick={() => { window.location.href = `/${locale}`; }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Voltar
        </a>

        <div style={S.hero}>
          <div style={{ ...S.heroIcon, background: `${item.category_color || T.primary}15` }}>
            {item.icon_url ? <img src={item.icon_url} alt="" style={{ width: 48, height: 48 }} /> :
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={item.category_color || T.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
              </svg>}
          </div>
          <div style={S.heroInfo}>
            <h1 style={S.heroTitle}>{item.name}</h1>
            <p style={S.heroDesc}>{item.short_description}</p>
            <div style={S.badges}>
              <span style={S.badge(item.category_color || T.primary)}>{item.category_name || item.category_slug}</span>
              <span style={S.badge(st.color)}>{st.label}</span>
              {item.min_plan !== 'free' && <span style={S.badge(T.purple)}>{item.min_plan.toUpperCase()}</span>}
              {item.is_popular === 1 && <span style={S.badge(T.warning)}>Popular</span>}
            </div>
          </div>
        </div>

        <div style={S.layout}>
          <div style={S.main}>
            {item.description && (
              <div style={S.section}>
                <h2 style={S.sectionTitle}>Sobre</h2>
                {renderMarkdown(item.description)}
              </div>
            )}

            {features.length > 0 && (
              <div style={S.section}>
                <h2 style={S.sectionTitle}>Funcionalidades</h2>
                <ul style={S.featureList}>
                  {features.map((f, i) => (
                    <li key={i} style={S.featureItem}>
                      <svg style={S.featureCheck} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {item.setup_guide && (
              <div style={S.section}>
                <div style={S.setupToggle} onClick={() => setShowSetup(!showSetup)}>
                  <span>Como configurar</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    style={{ transform: showSetup ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {showSetup && <div style={S.setupContent}>{renderMarkdown(item.setup_guide)}</div>}
              </div>
            )}
          </div>

          <div style={S.sidebar}>
            <div style={S.sidebarCard}>
              <a style={S.cta} href={item.status === 'available' ? 'https://example.com/integrations' : '#'}>
                {item.status === 'available' ? 'Configurar integracao' : 'Em breve'}
              </a>
            </div>

            {item.website_url && (
              <div style={S.sidebarCard}>
                <div style={S.sidebarTitle}>Website</div>
                <a href={item.website_url} target="_blank" rel="noopener" style={{ color: T.primary, fontSize: 14, wordBreak: 'break-all' as const }}>{item.website_url}</a>
              </div>
            )}

            <div style={S.sidebarCard}>
              <div style={S.sidebarTitle}>Plano minimo</div>
              <span style={S.badge(item.min_plan === 'free' ? T.success : T.purple)}>{item.min_plan === 'free' ? 'Gratuito' : item.min_plan.toUpperCase()}</span>
            </div>

            {related.length > 0 && (
              <div style={S.sidebarCard}>
                <div style={S.sidebarTitle}>Relacionadas</div>
                {related.map(r => (
                  <div key={r.id} style={S.relatedCard} onClick={() => { window.location.href = `/${locale}/${r.slug}`; }}>
                    <div style={{ ...S.relatedIcon, background: `${r.category_color || T.primary}15` }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={r.category_color || T.primary} strokeWidth="1.5">
                        <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>{r.name || r.slug}</div>
                      <div style={{ fontSize: 12, color: T.textMuted }}>{r.category_name}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <footer style={S.footer}>
          <p>&copy; {new Date().getFullYear()} WhiteLabelClaw. Integracoes.</p>
        </footer>
      </div>
    </div>
  );
}
